import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitleDefault from '../default/DialogTitleDefault';
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EmailIcon from '@mui/icons-material/Email';
import { showModal } from 'react-redux-modal-provider';
import { toast } from 'react-toastify';
import { ButtonBasic } from '../../components';
import NotifyBajaDAService from '../../services/notifyBajaDA.service';
import ModalRechazoDARegistro from './ModalRechazoDARegistro';
import ModalPreviewTicketDebitoRechazado from '../softland/ModalPreviewTicketDebitoRechazado';
import ModalPreviewEmailDebitoRechazado from '../softland/ModalPreviewEmailDebitoRechazado';

const ModalRechazoDAGroup = ({ show, hideModal, keyGroup, onRefresh }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmEliminar, setConfirmEliminar] = useState({ open: false, item: null });
    const [menuState, setMenuState] = useState({ anchor: null, item: null });
    const [templates, setTemplates] = useState({ email: null, ticket: null });
    const onOpenMenu = (e, item) => setMenuState({ anchor: e.currentTarget, item });
    const onCloseMenu = () => setMenuState({ anchor: null, item: null });

    const onVerTicket = (item) => {
        onCloseMenu();
        const row = {
            nombre: item?.nombre_nbda,
            contrato: item?.contratoId_nbda,
            cuenta: item?.cuentaId_nbda,
            email: item?.email_nbda,
            motivo: item?.motivo_nbda,
        };
        showModal(ModalPreviewTicketDebitoRechazado, { row, template: templates.ticket });
    };

    const onVerEmail = (item) => {
        onCloseMenu();
        const row = {
            contrato: item?.contratoId_nbda,
            cuenta: item?.cuentaId_nbda,
            motivo: item?.motivo_nbda,
            email: item?.email_nbda,
        };
        showModal(ModalPreviewEmailDebitoRechazado, { row, template: templates.email });
    };

    const handleClose = () => hideModal();

    const fetchItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const resp = await NotifyBajaDAService.getByGroup(keyGroup);
            setItems(resp?.registros || []);
            setTemplates({
              email: resp?.templateEmail || null,
              ticket: resp?.templateTicket || null
            });
        } catch (err) {
            const msg = err?.msg || 'No se pudo cargar el grupo.';
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    }, [keyGroup]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const onSaved = () => {
        fetchItems();
        if (onRefresh) onRefresh();
    };

    const onAgregar = () => {
        showModal(ModalRechazoDARegistro, { keyGroup, mode: 'create', onSaved });
    };

    const onEditar = (item) => {
        showModal(ModalRechazoDARegistro, { keyGroup, mode: 'edit', item, onSaved });
    };

    const onEliminar = (item) => {
        setConfirmEliminar({ open: true, item });
    };

    const onConfirmEliminar = async () => {
        const { item } = confirmEliminar;
        setConfirmEliminar({ open: false, item: null });
        try {
            await NotifyBajaDAService.deleteRegistro(item.id_nbda);
            toast.success('Registro eliminado');
            onSaved();
        } catch (err) {
            const msg = err?.response?.data?.msg || 'No se pudo eliminar el registro.';
            toast.error(msg);
        }
    };

    const total = items.length;
    const enviados = items.filter((i) => i.isSend_nbda).length;
    const pendientes = total - enviados;

    return (
        <>
        <Dialog open={show} disableEscapeKeyDown maxWidth='lg' fullWidth>
            <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogTitleDefault id='detalle-grupo-rda' onClose={handleClose}>
                    <Typography sx={{ flex: 1, textAlign: 'center', color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold' }} variant='h5'>
                        Grupo <span style={{ fontFamily: 'monospace', color: isDarkMode ? '#90caf9' : '#1976d2' }}>{keyGroup}</span>
                    </Typography>
                </DialogTitleDefault>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogContent>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={`Total: ${total}`} size='small' color='primary' />
                        <Chip label={`Enviados: ${enviados}`} size='small' color='success' />
                        <Chip label={`Pendientes: ${pendientes}`} size='small' color='primary' />
                        <Box sx={{ flex: 1 }} />
                        <ButtonBasic
                            textButton='Agregar registro'
                            size='small'
                            color='primary'
                            iconStart={<AddIcon fontSize='small' />}
                            onClick={onAgregar}
                        />
                    </Stack>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 460, background: isDarkMode ? '#253147' : '#fff' }}>
                            <Table stickyHeader size='small'>
                                <TableHead>
                                    <TableRow>
                                        {['Contrato', 'Cuenta', 'Nombre', 'Email', 'Motivo', 'Estado', 'Ticket #', 'Acciones'].map((h) => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    background: isDarkMode ? '#1F2A40' : '#f0f0f0',
                                                    color: isDarkMode ? '#fff' : '#111',
                                                    fontWeight: 'bold',
                                                }}
                                                align={h === 'Acciones' ? 'center' : 'left'}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align='center' sx={{ color: isDarkMode ? '#9aa8be' : '#666', py: 4 }}>
                                                No hay registros en este grupo.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {items.map((r) => {
                                        const isSent = r.isSend_nbda;
                                        return (
                                            <TableRow key={r.id_nbda}>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.contratoId_nbda || '-'}</TableCell>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.cuentaId_nbda || '-'}</TableCell>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.nombre_nbda || '-'}</TableCell>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.email_nbda || '-'}</TableCell>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.motivo_nbda || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={r.estado_nbda || 'PENDIENTE'}
                                                        size='small'
                                                        sx={{
                                                            background: isSent ? '#2e7d32' : isDarkMode ? '#1565c0' : '#1976d2',
                                                            color: '#fff',
                                                            fontSize: 11,
                                                            fontWeight: 'bold',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111', fontFamily: 'monospace' }}>
                                                    {r.ticketId_nbda || '-'}
                                                </TableCell>
                                                <TableCell align='center'>
                                                    {isSent ? (
                                                        <Tooltip title='Enviado, no editable'>
                                                            <LockIcon fontSize='small' sx={{ color: isDarkMode ? '#9aa8be' : '#999' }} />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title='Acciones'>
                                                            <IconButton size='small' onClick={(e) => onOpenMenu(e, r)} sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                                                                <MoreVertIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogActions sx={{ p: 2 }}>
                    <ButtonBasic
                        textButton='Cerrar'
                        variant='outlined'
                        color='inherit'
                        onClick={handleClose}
                    />
                </DialogActions>
            </Box>
        </Dialog>

        <Menu
            anchorEl={menuState.anchor}
            open={Boolean(menuState.anchor)}
            onClose={onCloseMenu}
            PaperProps={{
                sx: {
                    background: isDarkMode ? '#1F2A40' : '#fff',
                    border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                    minWidth: 180,
                },
            }}
        >
            <MenuItem onClick={() => { onCloseMenu(); onVerTicket(menuState.item); }}>
                <ListItemIcon><ConfirmationNumberIcon fontSize='small' sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} /></ListItemIcon>
                <ListItemText sx={{ color: isDarkMode ? '#e0e0e0' : '#111' }}>Preview ticket</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { onCloseMenu(); onVerEmail(menuState.item); }}>
                <ListItemIcon><EmailIcon fontSize='small' sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} /></ListItemIcon>
                <ListItemText sx={{ color: isDarkMode ? '#e0e0e0' : '#111' }}>Preview email</ListItemText>
            </MenuItem>
            <Divider sx={{ background: isDarkMode ? '#3d4a62' : '#e0e0e0' }} />
            <MenuItem onClick={() => { onCloseMenu(); onEditar(menuState.item); }}>
                <ListItemIcon><EditIcon fontSize='small' sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} /></ListItemIcon>
                <ListItemText sx={{ color: isDarkMode ? '#e0e0e0' : '#111' }}>Editar</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { onCloseMenu(); onEliminar(menuState.item); }}>
                <ListItemIcon><DeleteIcon fontSize='small' sx={{ color: isDarkMode ? '#f87171' : '#d32f2f' }} /></ListItemIcon>
                <ListItemText sx={{ color: isDarkMode ? '#f87171' : '#d32f2f' }}>Eliminar</ListItemText>
            </MenuItem>
        </Menu>

        <Dialog open={confirmEliminar.open} onClose={() => setConfirmEliminar({ open: false, item: null })} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ background: isDarkMode ? '#1F2A40' : '#fff', color: isDarkMode ? '#fff' : '#111' }}>
                Eliminar registro
            </DialogTitle>
            <DialogContent sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogContentText sx={{ color: isDarkMode ? '#9aa8be' : '#666' }}>
                    ¿Eliminar el registro del contrato{' '}
                    <strong style={{ fontFamily: 'monospace', color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                        {confirmEliminar.item?.contratoId_nbda || '-'}
                    </strong>?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ background: isDarkMode ? '#1F2A40' : '#fff', p: 2 }}>
                <ButtonBasic
                    textButton='Cancelar'
                    variant='outlined'
                    color='inherit'
                    onClick={() => setConfirmEliminar({ open: false, item: null })}
                />
                <ButtonBasic
                    textButton='Eliminar'
                    color='error'
                    onClick={onConfirmEliminar}
                />
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ModalRechazoDAGroup;
